import { useState, useCallback, useEffect } from 'react';
import { useRegisterSubDocumentSearch } from '../../../../search/context/SubDocumentSearchContext.js';

const usePDFSearch = ({ pdf, currentPage, scrollToPage }) => {
  const [textItemPositions, setTextItemPositions] = useState({});
  const [textItemMatchData, setTextItemMatchData] = useState({});
  const [pagesTextContent, setPagesTextContent] = useState([]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [matchIndexMapping, setMatchIndexMapping] = useState({});
  const processPageTextContent = useCallback((textContent, pageNum, pageRotation) => {
    let currentPos = 0;
    const itemsWithPositions = [];
    let fullText = "";
    let lastTransformY = null;
    let lastTransformX = null;
    let lastItemEndsWithHyphen = false;
    const isRotated = pageRotation === 90 || pageRotation === 270;
    textContent.items.forEach((item, idx) => {
      if ("str" in item && item.str) {
        const transformX = item.transform?.[4];
        const transformY = item.transform?.[5];
        const isNewLine = isRotated ? lastTransformX !== null && Math.abs(transformX - lastTransformX) > item.height / 2 : lastTransformY !== null && Math.abs(transformY - lastTransformY) > item.height / 2;
        if (isNewLine && !lastItemEndsWithHyphen && idx > 0) {
          fullText += " ";
          currentPos += 1;
        }
        itemsWithPositions.push({
          itemIndex: idx,
          start: currentPos,
          end: currentPos + item.str.length,
          text: item.str
        });
        fullText += item.str;
        currentPos += item.str.length;
        lastTransformY = transformY;
        lastTransformX = transformX;
        lastItemEndsWithHyphen = item.str.endsWith("-") && item.hasEOL;
      }
    });
    return {
      items: itemsWithPositions,
      fullText
    };
  }, []);
  const processPages = useCallback(async () => {
    if (!pdf) return;
    const pageCount = pdf.numPages;
    const positions = {};
    const pageTexts = [];
    for (let i = 0; i < pageCount; i++) {
      try {
        const pageNum = i + 1;
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({
          scale: 1
        });
        const pageRotation = viewport.rotation;
        const textContent = await page.getTextContent();
        const pageMapping = processPageTextContent(textContent, pageNum, pageRotation);
        positions[pageNum] = pageMapping;
        pageTexts.push(pageMapping.fullText);
      } catch (error) {
        console.error("Error processing page:", error);
        pageTexts.push("");
      }
    }
    setTextItemPositions(positions);
    setPagesTextContent(pageTexts);
  }, [
    pdf,
    processPageTextContent
  ]);
  const createMatchGroup = useCallback((pageNum, matchStart, matchEnd, items, groupId, matchData) => {
    const affectedItems = items.filter((item) => item.start < matchEnd && item.end > matchStart);
    const matchPositions = [];
    affectedItems.forEach((item) => {
      const itemMatchStart = Math.max(matchStart, item.start);
      const itemMatchEnd = Math.min(matchEnd, item.end);
      const relativeStart = itemMatchStart - item.start;
      const relativeEnd = itemMatchEnd - item.start;
      if (relativeEnd > relativeStart) {
        if (!matchData[pageNum][item.itemIndex]) {
          matchData[pageNum][item.itemIndex] = {
            ranges: []
          };
        }
        matchData[pageNum][item.itemIndex].groupId = groupId;
        const rangeIndex = matchData[pageNum][item.itemIndex]?.ranges.length || 0;
        matchPositions.push({
          pageNumber: pageNum,
          itemIndex: item.itemIndex,
          rangeIndex
        });
        matchData[pageNum][item.itemIndex].ranges.push([
          relativeStart,
          relativeEnd
        ]);
      }
    });
    return matchPositions;
  }, []);
  const mapSearchResultsToItems = useCallback((regex) => {
    const matchData = {};
    const indexMap = {};
    let totalMatches = 0;
    let currentGroupId = 0;
    Object.entries(textItemPositions).forEach(([pageNumStr, pageData]) => {
      const pageNum = parseInt(pageNumStr);
      matchData[pageNum] = {};
      const { fullText, items } = pageData;
      const clonedRegex = new RegExp(regex.source, regex.flags);
      let match;
      while ((match = clonedRegex.exec(fullText)) !== null) {
        if (!clonedRegex.global) break;
        const matchStart = match.index;
        const matchEnd = match.index + match[0].length;
        currentGroupId++;
        const matchPositions = createMatchGroup(pageNum, matchStart, matchEnd, items, currentGroupId, matchData);
        if (matchPositions.length > 0) {
          indexMap[totalMatches] = {
            positions: matchPositions
          };
          totalMatches++;
        }
      }
    });
    const matchesByPage = Object.entries(matchData).map(([pageNum, items]) => {
      const uniqueGroups = /* @__PURE__ */ new Set();
      Object.values(items).forEach((item) => {
        if (item.groupId) uniqueGroups.add(item.groupId);
      });
      return {
        page: parseInt(pageNum),
        matches: uniqueGroups.size
      };
    });
    return {
      matchData,
      indexMap,
      totalMatches,
      matches: matchesByPage
    };
  }, [
    textItemPositions,
    createMatchGroup
  ]);
  const findFirstMatchFromCurrentPage = useCallback((indexMap, totalMatches) => {
    for (let i = 0; i < totalMatches; i++) {
      const matchGroup = indexMap[i];
      if (matchGroup && matchGroup.positions.some((pos) => pos.pageNumber >= currentPage)) {
        return {
          group: matchGroup,
          index: i
        };
      }
    }
    return {
      group: null,
      index: -1
    };
  }, [
    currentPage
  ]);
  useRegisterSubDocumentSearch({
    onSearch: useCallback((regex) => {
      if (!regex) {
        setActiveMatch(null);
        return Promise.resolve({
          totalMatches: 0,
          currentIndex: null
        });
      }
      const { matchData, indexMap, totalMatches } = mapSearchResultsToItems(regex);
      setTextItemMatchData(matchData);
      setMatchIndexMapping(indexMap);
      const { group, index } = findFirstMatchFromCurrentPage(indexMap, totalMatches);
      if (group) {
        setActiveMatch(group);
        if (scrollToPage) {
          const firstPosition = group.positions[0];
          scrollToPage(firstPosition.pageNumber);
        }
      } else {
        setActiveMatch(null);
      }
      return Promise.resolve({
        totalMatches,
        currentIndex: index >= 0 ? index : null
      });
    }, [
      mapSearchResultsToItems,
      findFirstMatchFromCurrentPage,
      scrollToPage
    ]),
    onGoToMatch: useCallback((index) => {
      const matchGroup = matchIndexMapping[index];
      if (matchGroup) {
        setActiveMatch(matchGroup);
        if (scrollToPage) {
          const firstPosition = matchGroup.positions[0];
          scrollToPage(firstPosition.pageNumber);
        }
      }
    }, [
      matchIndexMapping,
      scrollToPage
    ])
  });
  useEffect(() => {
    if (pdf) {
      processPages();
    }
  }, [
    pdf,
    processPages
  ]);
  return {
    textItemPositions,
    textItemMatchData,
    pagesTextContent,
    activeMatch,
    matchIndexMapping,
    processPages,
    mapSearchResultsToItems
  };
};

export { usePDFSearch as default, usePDFSearch };
//# sourceMappingURL=usePDFSearch.js.map
